<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useNow, useResizeObserver } from '@vueuse/core';
import { ArrowUpRight } from 'lucide-vue-next';
import CopyValueButton from '@/components/record/fields/CopyValueButton.vue';
import { listItemText, parseLink, timeInZone } from '@/components/record/fields/formatFieldValue';
import type { FieldKind } from '@/components/record/fields/fieldKind';
import { openExternal } from '@/utils/nativeShell';

/**
 * Every kind in the text family, which differ only in formatting:
 *
 * - text wraps;
 * - long text shows three lines, then "Show more";
 * - a code and a network address are monospace, with a copy button;
 * - a link shows its domain and opens in a new tab;
 * - an email is a mailto link, with a copy button;
 * - a time zone says what time it is there now;
 * - a list of values shows up to five chips, then "+3 more".
 *
 * Links and emails open through `openExternal`, and copy buttons copy through
 * `copyText`, so a later Electron or Capacitor build changes only those.
 */
const props = defineProps<{
    kind: FieldKind;
    label: string;
    value: unknown;
}>();

const CHIP_LIMIT = 5;

const text = computed(() => (props.value === null || props.value === undefined ? '' : String(props.value)));
const link = computed(() => (props.kind === 'link' ? parseLink(text.value) : null));

function openLink(event: MouseEvent, url: string) {
    event.preventDefault();
    event.stopPropagation();
    openExternal(url);
}

const now = useNow({ interval: 60_000 });
const zoneTime = computed(() => (props.kind === 'timeZone' ? timeInZone(text.value, now.value) : null));

const listItems = computed(() => (Array.isArray(props.value) ? props.value.map(listItemText) : [text.value]));
const showAllChips = ref(false);
const shownChips = computed(() => (showAllChips.value ? listItems.value : listItems.value.slice(0, CHIP_LIMIT)));
const hiddenChipCount = computed(() => listItems.value.length - shownChips.value.length);

// Long text is clamped to three lines. "Show more" appears only when the
// clamp actually hides something at the width the field has.
const longTextElement = ref<HTMLElement | null>(null);
const expanded = ref(false);
const overflows = ref(false);
function measureOverflow() {
    const element = longTextElement.value;
    if (element && !expanded.value) {
        overflows.value = element.scrollHeight > element.clientHeight + 1;
    }
}
onMounted(() => nextTick(measureOverflow));
useResizeObserver(longTextElement, measureOverflow);
watch(text, () => nextTick(measureOverflow));
</script>

<template>
  <span v-if="kind === 'longText'" class="flex min-w-0 flex-col items-start gap-0.5">
    <span
      ref="longTextElement"
      data-testid="long-text-value"
      class="whitespace-pre-line [overflow-wrap:anywhere]"
      :class="!expanded && 'line-clamp-3'"
    >{{ text }}</span>
    <button
      v-if="overflows || expanded"
      type="button"
      data-testid="long-text-toggle"
      class="rounded text-xs text-muted-foreground hover:text-foreground hover:underline focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none"
      @click="expanded = !expanded"
    >{{ expanded ? 'Show less' : 'Show more' }}</button>
  </span>

  <span v-else-if="kind === 'code' || kind === 'networkAddress'" class="inline-flex max-w-full min-w-0 items-center gap-1.5">
    <span data-testid="code-value" class="font-mono text-[13px] [overflow-wrap:anywhere]">{{ text }}</span>
    <CopyValueButton :text="text" :label="label" />
  </span>

  <a
    v-else-if="kind === 'link' && link"
    data-testid="link-value"
    :href="link.href"
    target="_blank"
    rel="noopener noreferrer"
    :title="link.href"
    class="inline-flex max-w-full min-w-0 items-center gap-1 underline-offset-2 hover:underline"
    @click="openLink($event, link.href)"
  >
    <span class="min-w-0 [overflow-wrap:anywhere]">{{ link.domain }}</span>
    <ArrowUpRight class="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
  </a>

  <span v-else-if="kind === 'email'" class="inline-flex max-w-full min-w-0 items-center gap-1.5">
    <a
      data-testid="email-value"
      :href="`mailto:${text}`"
      class="min-w-0 underline-offset-2 [overflow-wrap:anywhere] hover:underline"
      @click="openLink($event, `mailto:${text}`)"
    >{{ text }}</a>
    <CopyValueButton :text="text" :label="label" />
  </span>

  <span v-else-if="kind === 'timeZone'" data-testid="time-zone-value">
    {{ text }}<span v-if="zoneTime" class="text-muted-foreground"> · {{ zoneTime }} there now</span>
  </span>

  <span v-else-if="kind === 'listOfValues'" class="flex min-w-0 flex-wrap items-center gap-1">
    <span
      v-for="(item, index) in shownChips"
      :key="index"
      data-testid="value-chip"
      class="inline-flex h-5 max-w-full items-center rounded bg-muted px-1.5 text-xs [overflow-wrap:anywhere]"
    >{{ item }}</span>
    <button
      v-if="hiddenChipCount > 0"
      type="button"
      data-testid="value-chips-more"
      class="rounded px-1 text-xs text-muted-foreground hover:text-foreground hover:underline focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none"
      @click="showAllChips = true"
    >+{{ hiddenChipCount }} more</button>
  </span>

  <span v-else class="[overflow-wrap:anywhere]" data-testid="text-value">{{ text }}</span>
</template>
